import { Controller } from "@hotwired/stimulus";

// See https://developers.google.com/maps/documentation/javascript/place-autocomplete

export default class extends Controller {
  static targets = [
    "street",
    "houseNumber",
    "extendedAddress",
    "postcode",
    "city",
    "province",
    "country"
  ];

  static values = { apiKey: String };

  // Shared across instances so the Maps script is only injected once per page, even if
  // the controller re-connects (e.g. the form is re-rendered).
  static mapsLoaderPromise = null;

  async connect() {
    if (!this.apiKeyValue || !this.hasStreetTarget) return;

    try {
      await this.loadGoogleMaps(this.apiKeyValue, document.documentElement.lang);
      this.initAutocomplete();
    } catch (error) {
      // Degrade silently to manual entry; the form remains fully functional.
      console.error(error);
    }
  }

  loadGoogleMaps(apiKey, language) {
    if (window.google?.maps?.places) return Promise.resolve();
    if (this.constructor.mapsLoaderPromise) return this.constructor.mapsLoaderPromise;

    this.constructor.mapsLoaderPromise = new Promise((resolve, reject) => {
      const callbackName = "__wgGoogleMapsReady";
      window[callbackName] = () => resolve();

      const params = new URLSearchParams({
        key: apiKey,
        libraries: "places",
        loading: "async",
        callback: callbackName
      });
      if (language) params.set("language", language);

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.onerror = () => {
        this.constructor.mapsLoaderPromise = null;
        reject(new Error("Failed to load the Google Maps JS API"));
      };
      document.head.appendChild(script);
    });

    return this.constructor.mapsLoaderPromise;
  }

  disconnect() {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  }

  initAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(this.streetTarget, {
      fields: ["address_components"],
      types: ["address"]
    });
    this.autocomplete.addListener("place_changed", () => this.fillInAddress());
  }

  fillInAddress() {
    const components = this.autocomplete.getPlace()?.address_components;
    if (!components) return;

    const componentOf = (type) => components.find((component) => component.types.includes(type));

    const houseNumber = componentOf("street_number")?.long_name || "";
    const street = componentOf("route")?.long_name || "";
    const postcode = componentOf("postal_code")?.long_name || "";
    // Larger UK addresses expose the town as "postal_town" rather than "locality".
    const city = (componentOf("locality") || componentOf("postal_town"))?.long_name || "";
    const province = componentOf("administrative_area_level_1")?.long_name || "";
    const countryCode = componentOf("country")?.short_name || "";

    // Google returns the street name in "route" and the number separately, which maps
    // cleanly onto our split street/house_number columns.
    this.setFieldValue(this.streetTarget, street);
    if (this.hasHouseNumberTarget) this.setFieldValue(this.houseNumberTarget, houseNumber);
    if (this.hasPostcodeTarget) this.setFieldValue(this.postcodeTarget, postcode);
    if (this.hasCityTarget) this.setFieldValue(this.cityTarget, city);
    if (this.hasProvinceTarget) this.setFieldValue(this.provinceTarget, province);
    if (this.hasCountryTarget && countryCode) this.setCountryValue(countryCode);

    // Move focus to the optional apartment/suite line after the autofill.
    // The Places widget only closes its suggestions dropdown when the street input fires a
    // real blur, which doesn't happen when a suggestion is picked by mouse click. Deferring
    // to the next frame and re-focusing the street input before moving on guarantees that
    // blur, so the dropdown closes in both the keyboard and mouse-click cases.
    if (this.hasExtendedAddressTarget) {
      requestAnimationFrame(() => {
        this.streetTarget.focus();
        this.extendedAddressTarget.focus();
      });
    }
  }

  setFieldValue(field, value) {
    field.value = value;
    // Fire input & change so Parsley re-validates and clears any earlier error state.
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }

  setCountryValue(code) {
    const selectize = this.countryTarget.selectize;
    selectize.setValue(code, false);
  }
}
